// src/pages/Admin/SubscriptionManagement/SubscriptionAdminPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../../api/axiosConfig';
import { toast } from 'react-toastify';
import { 
  UsersIcon, 
  ClockIcon, 
  ExclamationTriangleIcon, 
  LockClosedIcon, 
  BanknotesIcon, 
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  SparklesIcon,
  PlusCircleIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import PageLayout, { PageHeader, StatsGrid, Toolbar, TableCard } from '../../../components/common/PageLayout';

export default function SubscriptionAdminPage() {
  const [data, setData] = useState({
    metrics: {
      totalSubscribers: 0,
      activeTrials: 0,
      expiringSoon: 0,
      expiredCount: 0,
      paidActiveCount: 0,
      totalRevenue: 0
    },
    subscriptions: [],
    transactions: []
  });

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, trialing, expiring_soon, expired, active

  // Modals state
  const [selectedSubForExtend, setSelectedSubForExtend] = useState(null);
  const [extendDays, setExtendDays] = useState(14);
  const [isExtending, setIsExtending] = useState(false);

  const [selectedSubForAssign, setSelectedSubForAssign] = useState(null);
  const [assignPlanId, setAssignPlanId] = useState(2);
  const [assignDurationDays, setAssignDurationDays] = useState(30);
  const [isAssigning, setIsAssigning] = useState(false);

  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/subscriptions/admin/subscribers');
      setData(res.data);
    } catch (err) {
      console.error('Failed to load subscription admin data:', err);
      toast.error('Failed to fetch subscription records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  // Handle Extend Trial
  const handleExtendTrial = async (e) => {
    e.preventDefault();
    if (!selectedSubForExtend) return;

    try {
      setIsExtending(true);
      const res = await api.post('/subscriptions/admin/extend-trial', {
        subscription_id: selectedSubForExtend.id,
        days: extendDays
      });
      if (res.data?.success) {
        toast.success(res.data.message);
        setSelectedSubForExtend(null);
        await fetchAdminData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to extend trial');
    } finally {
      setIsExtending(false);
    }
  };

  // Handle Manual Assign Plan
  const handleAssignPlan = async (e) => {
    e.preventDefault();
    if (!selectedSubForAssign) return;

    try {
      setIsAssigning(true);
      const res = await api.post('/subscriptions/admin/assign-plan', {
        subscription_id: selectedSubForAssign.id,
        plan_id: assignPlanId,
        duration_days: assignDurationDays
      });
      if (res.data?.success) {
        toast.success(res.data.message);
        setSelectedSubForAssign(null);
        await fetchAdminData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign plan');
    } finally {
      setIsAssigning(false);
    }
  };

  // Filtering
  const subscriptionsList = Array.isArray(data?.subscriptions) ? data.subscriptions : [];
  const filteredSubscriptions = subscriptionsList.filter(sub => {
    const matchesSearch = 
      (sub.user_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.user_email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (sub.company_name || '').toLowerCase().includes(searchQuery.toLowerCase());

    if (!matchesSearch) return false;

    if (statusFilter === 'ALL') return true;
    if (statusFilter === 'trialing') return sub.status === 'trialing' && sub.days_remaining > 5;
    if (statusFilter === 'expiring_soon') return sub.status === 'trialing' && sub.days_remaining <= 5 && sub.days_remaining > 0;
    if (statusFilter === 'expired') return sub.status === 'expired' || (sub.status === 'trialing' && sub.days_remaining <= 0);
    if (statusFilter === 'active') return sub.status === 'active';
    return true;
  });

  const STATUS_TABS = ['All Tenants', 'Active Trial', 'Expiring Soon', 'Expired / Locked', 'Paid Active'];
  const TAB_TO_STATUS = {
    'All Tenants': 'ALL',
    'Active Trial': 'trialing',
    'Expiring Soon': 'expiring_soon',
    'Expired / Locked': 'expired',
    'Paid Active': 'active'
  };
  const STATUS_TO_TAB = {
    'ALL': 'All Tenants',
    'trialing': 'Active Trial',
    'expiring_soon': 'Expiring Soon',
    'expired': 'Expired / Locked',
    'active': 'Paid Active'
  };

  const statsList = useMemo(() => [
    {
      label: 'Total Tenants',
      value: data.metrics.totalSubscribers,
      icon: UsersIcon,
      variant: 'default',
      isActive: statusFilter === 'ALL' || !statusFilter,
      onClick: () => setStatusFilter('ALL'),
    },
    {
      label: 'Active Trials',
      value: data.metrics.activeTrials,
      icon: ClockIcon,
      variant: 'orange',
      isActive: statusFilter === 'trialing',
      onClick: () => setStatusFilter(s => s === 'trialing' ? 'ALL' : 'trialing'),
    },
    {
      label: 'Expiring Soon',
      value: data.metrics.expiringSoon,
      icon: ExclamationTriangleIcon,
      variant: 'amber',
      isActive: statusFilter === 'expiring_soon',
      onClick: () => setStatusFilter(s => s === 'expiring_soon' ? 'ALL' : 'expiring_soon'),
    },
    {
      label: 'Locked Accounts',
      value: data.metrics.expiredCount,
      icon: LockClosedIcon,
      variant: 'red',
      isActive: statusFilter === 'expired',
      onClick: () => setStatusFilter(s => s === 'expired' ? 'ALL' : 'expired'),
    },
    {
      label: 'Total Revenue',
      value: `₹${(data.metrics.totalRevenue || 0).toLocaleString('en-IN')}`,
      icon: BanknotesIcon,
      variant: 'green',
      isActive: statusFilter === 'active',
      onClick: () => setStatusFilter(s => s === 'active' ? 'ALL' : 'active'),
    },
  ], [data.metrics, statusFilter]);

  return (
    <PageLayout>
      
      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <PageHeader
        icon={SparklesIcon}
        iconColor="text-[#E8612C]"
        title="SaaS & Subscription Management"
        subtitle="Monitor tenant 29-day free trials, access lockouts, plan tiers, and manage manual extensions."
        onRefresh={fetchAdminData}
        loading={loading}
      />

      {/* ── METRICS / STATS ─────────────────────────────────────────────────── */}
      <StatsGrid stats={statsList} cols={5} />

      {/* ── TOOLBAR ─────────────────────────────────────────────────────────── */}
      <Toolbar
        searchTerm={searchQuery}
        onSearch={setSearchQuery}
        searchPlaceholder="Search by company or email..."
        tabs={STATUS_TABS}
        activeTab={STATUS_TO_TAB[statusFilter] || 'All Tenants'}
        onTabChange={(tab) => setStatusFilter(TAB_TO_STATUS[tab] || 'ALL')}
      />

      {/* ── MAIN SUBSCRIPTIONS TABLE ───────────────────────────────────────── */}
      <TableCard>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-50/75 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-bold">
              <tr>
                <th className="py-3.5 px-5">Tenant / Company</th>
                <th className="py-3.5 px-4">Plan Assigned</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-4">Trial Started</th>
                <th className="py-3.5 px-4">Trial / Period Ends</th>
                <th className="py-3.5 px-4">Time Left</th>
                <th className="py-3.5 px-5 text-right whitespace-nowrap min-w-[230px]">Super Admin Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-medium">
              {filteredSubscriptions.length > 0 ? (
                filteredSubscriptions.map((sub) => {
                  const isExpiringSoon = sub.status === 'trialing' && sub.days_remaining <= 5 && sub.days_remaining > 0;
                  const isExpired = sub.is_expired || sub.status === 'expired' || (sub.status === 'trialing' && sub.days_remaining <= 0);
                  const isPaid = sub.status === 'active';

                  return (
                    <tr key={sub.id} className="hover:bg-gray-50/60 transition">
                      
                      {/* Company Info */}
                      <td className="py-4 px-5">
                        <div className="font-bold text-gray-900 text-sm">
                          {sub.company_name || sub.user_name}
                        </div>
                        <div className="text-gray-500 text-[11px] font-mono mt-0.5">
                          {sub.user_email}
                        </div>
                      </td>

                      {/* Plan */}
                      <td className="py-4 px-4">
                        <span className="font-bold text-gray-800 bg-gray-100 px-2.5 py-1 rounded-md text-[11px]">
                          {sub.plan_name || 'Standard'}
                        </span>
                      </td>

                      {/* Status Badge */}
                      <td className="py-4 px-4">
                        {isPaid ? (
                          <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-bold px-2.5 py-0.5 rounded-full">
                            <CheckBadgeIcon className="w-3.5 h-3.5 text-emerald-600" />
                            Paid Active
                          </span>
                        ) : isExpired ? (
                          <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-700 border border-red-200 text-[11px] font-bold px-2.5 py-0.5 rounded-full">
                            <LockClosedIcon className="w-3.5 h-3.5 text-red-600" />
                            Expired / Locked
                          </span>
                        ) : isExpiringSoon ? (
                          <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-bold px-2.5 py-0.5 rounded-full">
                            <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-600" />
                            Expiring Soon
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-bold px-2.5 py-0.5 rounded-full">
                            <ClockIcon className="w-3.5 h-3.5 text-blue-600" />
                            29-Day Trial
                          </span>
                        )}
                      </td>

                      {/* Trial Started */}
                      <td className="py-4 px-4 text-gray-500">
                        {sub.trial_started_at ? new Date(sub.trial_started_at).toLocaleDateString() : '—'}
                      </td>

                      {/* Trial Ends */}
                      <td className="py-4 px-4 text-gray-700 font-semibold">
                        {sub.trial_ends_at ? new Date(sub.trial_ends_at).toLocaleDateString() : '—'}
                      </td>

                      {/* Time Left */}
                      <td className="py-4 px-4">
                        {isPaid ? (
                          <span className="text-emerald-600 font-bold">Paid Active</span>
                        ) : isExpired ? (
                          <span className="text-red-600 font-bold">Access Locked</span>
                        ) : (
                          <span className={`font-bold ${isExpiringSoon ? 'text-amber-600' : 'text-[#E8612C]'}`}>
                            {sub.days_remaining} {sub.days_remaining === 1 ? 'day' : 'days'} left
                          </span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-5 text-right whitespace-nowrap">
                        <div className="inline-flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedSubForExtend(sub);
                              setExtendDays(14);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#E8612C] bg-orange-50 hover:bg-orange-100 border border-orange-200 transition-colors shadow-2xs"
                            title="Extend trial by X days"
                          >
                            <PlusCircleIcon className="w-3.5 h-3.5" />
                            Extend Trial
                          </button>

                          <button
                            onClick={() => {
                              setSelectedSubForAssign(sub);
                              setAssignPlanId(sub.plan_id || 2);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#1B2A41] bg-white hover:bg-slate-50 border border-slate-200 transition-colors shadow-2xs"
                            title="Manually grant or activate a plan"
                          >
                            <SparklesIcon className="w-3.5 h-3.5 text-indigo-500" />
                            Grant Plan
                          </button>
                        </div>
                      </td>

                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    No matching tenants found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </TableCard>

      {/* Payment Transactions Audit Log */}
      <TableCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-[#1B2A41] dark:text-white">
              Recent Subscription Payments & Ledger
            </h2>
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Complete transaction log of gateway checkouts and subscription orders.
            </p>
          </div>
          <span className="text-xs font-bold text-gray-400 dark:text-slate-500">
            {data.transactions.length} Transactions Recorded
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-50 dark:bg-[#161b22] text-gray-500 dark:text-slate-400 uppercase tracking-wider font-bold">
              <tr>
                <th className="py-2.5 px-4">Txn ID</th>
                <th className="py-2.5 px-4">User</th>
                <th className="py-2.5 px-4">Plan</th>
                <th className="py-2.5 px-4">Amount</th>
                <th className="py-2.5 px-4">Method / Gateway</th>
                <th className="py-2.5 px-4">Status</th>
                <th className="py-2.5 px-4">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-[#30363d]">
              {data.transactions.map((txn) => (
                <tr key={txn.id} className="hover:bg-gray-50/50 dark:hover:bg-[#1f242c]">
                  <td className="py-3 px-4 font-mono font-bold text-gray-700 dark:text-slate-300">{txn.transaction_id}</td>
                  <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">{txn.user_name || txn.user_email}</td>
                  <td className="py-3 px-4 font-bold text-gray-800 dark:text-slate-200">{txn.plan_name}</td>
                  <td className="py-3 px-4 font-black text-[#1B2A41] dark:text-[#E8612C]">₹{Number(txn.amount).toLocaleString('en-IN')}</td>
                  <td className="py-3 px-4 text-gray-600 dark:text-slate-400">{txn.payment_method || txn.gateway}</td>
                  <td className="py-3 px-4">
                    <span className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
                      {txn.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-400 dark:text-slate-500">{new Date(txn.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </TableCard>

      {/* MODAL 1: Extend Trial Modal */}
      {selectedSubForExtend && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 p-6 space-y-5">
            <div>
              <h3 className="text-lg font-bold text-[#1B2A41]">
                Extend Free Trial
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Extend trial access for <strong>{selectedSubForExtend.company_name || selectedSubForExtend.user_name}</strong>.
              </p>
            </div>

            <form onSubmit={handleExtendTrial} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
                  Select Days to Add
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[7, 14, 29, 60].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setExtendDays(d)}
                      className={`py-2 text-xs font-bold rounded-xl border transition ${
                        extendDays === d
                          ? 'border-[#E8612C] bg-[#FFF5F0] text-[#E8612C]'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      +{d} Days
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Or Custom Days
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={extendDays}
                  onChange={(e) => setExtendDays(Number(e.target.value))}
                  className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8612C]"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setSelectedSubForExtend(null)}
                  className="px-4 py-2 text-xs font-bold text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isExtending}
                  className="bg-[#E8612C] hover:bg-[#D55320] text-white text-xs font-bold px-4 py-2 rounded-xl transition shadow disabled:opacity-50"
                >
                  {isExtending ? 'Updating...' : `Confirm +${extendDays} Days`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Manual Plan Assignment */}
      {selectedSubForAssign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 p-6 space-y-5">
            <div>
              <h3 className="text-lg font-bold text-[#1B2A41]">
                Grant / Override Subscription Plan
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Assign complimentary or offline access to <strong>{selectedSubForAssign.company_name || selectedSubForAssign.user_name}</strong>.
              </p>
            </div>

            <form onSubmit={handleAssignPlan} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Select Plan Tier
                </label>
                <select
                  value={assignPlanId}
                  onChange={(e) => setAssignPlanId(Number(e.target.value))}
                  className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8612C]"
                >
                  <option value={1}>Starter Plan (Up to 5 Users)</option>
                  <option value={2}>Professional Plan (Up to 25 Users)</option>
                  <option value={3}>Enterprise Plan (Unlimited Users)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Access Duration (Days)
                </label>
                <select
                  value={assignDurationDays}
                  onChange={(e) => setAssignDurationDays(Number(e.target.value))}
                  className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8612C]"
                >
                  <option value={30}>30 Days (1 Month)</option>
                  <option value={90}>90 Days (3 Months)</option>
                  <option value={180}>180 Days (6 Months)</option>
                  <option value={365}>365 Days (1 Year)</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setSelectedSubForAssign(null)}
                  className="px-4 py-2 text-xs font-bold text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isAssigning}
                  className="bg-[#1B2A41] hover:bg-[#2B4158] text-white text-xs font-bold px-4 py-2 rounded-xl transition shadow disabled:opacity-50"
                >
                  {isAssigning ? 'Activating...' : 'Activate Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
